import { Controller } from "@hotwired/stimulus"

// Keeps the task's date pickers in step with the selected site: the days the site itself runs
// over are marked in the calendar, so it is obvious when a task falls outside them.
//
// The marked dates live on each picker's Stimulus value. date-picker re-reads them every time
// it opens, so rewriting the attribute here is enough — no cross-controller calls.
export default class extends Controller {
  static values = { maxDays: { type: Number, default: 750 } }

  setDates(event) {
    const id = event.target.value
    if (!id) {
      this.applyRange(null, null)
      return
    }

    fetch(`/sites/${id}`)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP error: ${response.status}`)
        return response.json()
      })
      .then((resp) => this.applyRange(resp.start_date, resp.end_date))
      .catch((error) => {
        console.error("Failed to fetch site dates:", error)
      })
  }

  applyRange(startDate, endDate) {
    const select = document.getElementById("task_site_id")
    if (select) {
      select.dataset.startDate = startDate || ""
      select.dataset.endDate = endDate || ""
    }

    const marked = JSON.stringify(this.datesBetween(startDate, endDate))
    // Only the task's own date fields, in every period row. A document-wide sweep would mark the
    // site's run into every other picker on the page — custom-field date filters, the global
    // expense offcanvas.
    const form = this.element.closest("form") ?? document
    this.markDateFields(form, marked)
    // The nested-form <template> row lives in a document fragment the sweep above never sees —
    // without this, a period row added after the site change keeps the old site's run.
    for (const template of form.querySelectorAll("template")) {
      this.markDateFields(template.content, marked)
    }
  }

  markDateFields(root, marked) {
    for (const attribute of ["start_date", "end_date"]) {
      const fields = root.querySelectorAll(`input[name^="task["][name$="[${attribute}]"]`)
      for (const field of fields) {
        field
          .closest("[data-date-picker-marked-dates-value]")
          ?.setAttribute("data-date-picker-marked-dates-value", marked)
      }
    }
  }

  // Inclusive list of ISO days. Bounded, because a site carrying the 9999-12-31 open-ended
  // sentinel would otherwise generate millions of entries and hang the tab.
  datesBetween(startDate, endDate) {
    if (!startDate || !endDate) return []

    const from = new Date(`${startDate}T00:00:00Z`)
    const to = new Date(`${endDate}T00:00:00Z`)
    if (isNaN(from) || isNaN(to) || to < from) return []

    const days = []
    const cursor = new Date(from)
    while (cursor <= to && days.length < this.maxDaysValue) {
      days.push(cursor.toISOString().slice(0, 10))
      cursor.setUTCDate(cursor.getUTCDate() + 1)
    }
    return days
  }

  submit() {
    this.element.closest("form").requestSubmit()
  }
}
